// src/pages/ServiceDetailPage.js
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { productById } from '../data/products';
import { otherServiceById, SERVICE_TONES } from '../data/otherServices';
import { PHONE_TEL, waLink } from '../data/contact';
import Reveal from '../components/layout/Reveal';
import { Breadcrumb, usePageTitle } from '../components/layout/PageBits';
import InsuranceProductPage from './InsuranceProductPage';
import NotFoundPage from './NotFoundPage';

// Investment and planning services: accent hero, what we do, and a callback card.
function SimpleServicePage({ service }) {
  const { lang, t } = useLang();
  const c = service[lang];
  const tone = SERVICE_TONES[service.tone];
  usePageTitle(c.title);
  const isInvest = service.group === 'invest';
  return (
    <>
      <section className={`${tone.soft} py-12 sm:py-16`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <Breadcrumb current={c.title} />
            <div className="text-5xl" aria-hidden="true">{service.emoji}</div>
            <h1 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight">{c.title}</h1>
            <p className="mt-4 text-lg text-slate-600 max-w-3xl leading-relaxed">{c.desc}</p>
          </Reveal>
        </div>
      </section>
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid md:grid-cols-[1.3fr_1fr] gap-8 items-start">
          <Reveal>
            <h2 className="text-2xl font-extrabold mb-5">{t('whatYouGet')}</h2>
            <ul className="space-y-3">
              {c.features.map((f) => <li key={f} className="flex gap-3 text-slate-700"><span className={`w-6 h-6 rounded-full text-white text-xs flex items-center justify-center shrink-0 ${tone.solid}`}>✓</span>{f}</li>)}
            </ul>
            {isInvest && <p className="mt-8 text-xs text-slate-500 border-l-4 border-amber-300 pl-3">{t('mfRisk')}</p>}
            {isInvest && (
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/calculators" className="bg-sky text-brand font-semibold px-4 py-2.5 rounded-xl">{t('navTools')} →</Link>
                <Link to="/mutual-funds" className="bg-sky text-brand font-semibold px-4 py-2.5 rounded-xl">{t('fundDash')} →</Link>
              </div>
            )}
          </Reveal>
          <Reveal className="bg-ink text-white rounded-3xl p-6 sm:p-8 shadow-xl">
            <h2 className="text-xl font-extrabold">{t('talkAbout')} {c.title}</h2>
            <p className="mt-2 text-white/70 text-sm">{t('talkAboutSub')}</p>
            <a href={waLink(t('waLead')(c.title, ''))} target="_blank" rel="noopener noreferrer" className="mt-6 block text-center bg-emerald-500 hover:bg-emerald-600 font-bold py-3 rounded-xl">💬 WhatsApp</a>
            <a href={PHONE_TEL} className="mt-3 block text-center bg-white/10 hover:bg-white/15 font-bold py-3 rounded-xl">📞 {t('call')}</a>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function ServiceDetailPage() {
  const { serviceId } = useParams();
  const product = productById(serviceId);
  if (product) return <InsuranceProductPage key={product.id} product={product} />;
  const service = otherServiceById(serviceId);
  if (service) return <SimpleServicePage key={service.id} service={service} />;
  return <NotFoundPage />;
}

export default ServiceDetailPage;
